import type {
  Arrangement,
  Cell,
  CubeColor,
  CubeHellPlayerId,
  CubeHellState,
  CubeHellViewerState,
  QueryAxis,
  ValidationResult,
} from "../types";
import {
  CELL_COUNT,
  CUBE_COLORS,
  CUBE_HELL_PLAYERS,
  CUBES_PER_COLOR,
  EMPTY_CELLS,
  GRID,
} from "../types";

export function isPlayerId(value: unknown): value is CubeHellPlayerId {
  return (
    typeof value === "string" &&
    CUBE_HELL_PLAYERS.includes(value as CubeHellPlayerId)
  );
}

export function getOpponentPlayerId(
  playerId: CubeHellPlayerId,
): CubeHellPlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

export function isCubeColor(value: unknown): value is CubeColor {
  return typeof value === "string" && CUBE_COLORS.includes(value as CubeColor);
}

export function isCell(value: unknown): value is Cell {
  return value === "empty" || isCubeColor(value);
}

// ---- Coordinates ------------------------------------------------------------

export interface Coord {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export function inRange(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value < GRID;
}

/** cellIndex = x + y*3 + z*9. */
export function cellIndex(x: number, y: number, z: number): number {
  return x + y * GRID + z * GRID * GRID;
}

export function coordOf(index: number): Coord {
  return {
    x: index % GRID,
    y: Math.floor(index / GRID) % GRID,
    z: Math.floor(index / (GRID * GRID)),
  };
}

export function axisValue(coord: Coord, axis: QueryAxis): number {
  return axis === "x" ? coord.x : axis === "y" ? coord.y : coord.z;
}

/** Face-adjacent (6-neighbour) cell indices, in ascending order. */
export function neighbourIndices(index: number): number[] {
  const { x, y, z } = coordOf(index);
  const deltas: ReadonlyArray<readonly [number, number, number]> = [
    [-1, 0, 0],
    [1, 0, 0],
    [0, -1, 0],
    [0, 1, 0],
    [0, 0, -1],
    [0, 0, 1],
  ];
  const result: number[] = [];
  for (const [dx, dy, dz] of deltas) {
    const nx = x + dx;
    const ny = y + dy;
    const nz = z + dz;
    if (inRange(nx) && inRange(ny) && inRange(nz)) {
      result.push(cellIndex(nx, ny, nz));
    }
  }
  return result.sort((a, b) => a - b);
}

/** True when `indices` form one 6-face-connected group (non-empty). */
export function isConnectedGroup(indices: readonly number[]): boolean {
  if (indices.length === 0) return false;
  const members = new Set(indices);
  const seen = new Set<number>();
  const stack = [indices[0]];
  seen.add(indices[0]);
  while (stack.length > 0) {
    const current = stack.pop() as number;
    for (const neighbour of neighbourIndices(current)) {
      if (members.has(neighbour) && !seen.has(neighbour)) {
        seen.add(neighbour);
        stack.push(neighbour);
      }
    }
  }
  return seen.size === members.size;
}

// ---- Deterministic PRNG (mulberry32) ----------------------------------------
/** Pure mulberry32 step: returns a value in [0,1) and the next state. */
export function nextRng(state: number): { value: number; state: number } {
  const a = (state + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, state: a };
}

// ---- Arrangement generation -------------------------------------------------

/** Free cell indices (ascending) whose value is still "empty". */
function freeCells(cells: readonly Cell[]): number[] {
  const result: number[] = [];
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i] === "empty") result.push(i);
  }
  return result;
}

/** Free cells (ascending) face-adjacent to any cell currently in `group`. */
function freeFrontier(
  group: readonly number[],
  cells: readonly Cell[],
): number[] {
  const seen = new Set<number>();
  for (const member of group) {
    for (const neighbour of neighbourIndices(member)) {
      if (cells[neighbour] === "empty") seen.add(neighbour);
    }
  }
  return [...seen].sort((a, b) => a - b);
}

interface BuildAttempt {
  readonly cells: Cell[] | null;
  readonly state: number;
}

/** One seeded attempt: grow each colour into a 6-connected 5-cell blob. */
function tryBuildArrangement(rngState: number): BuildAttempt {
  const cells: Cell[] = new Array<Cell>(CELL_COUNT).fill("empty");
  let state = rngState;
  for (const color of CUBE_COLORS) {
    const seeds = freeCells(cells);
    const seedStep = nextRng(state);
    state = seedStep.state;
    const seed = seeds[Math.floor(seedStep.value * seeds.length)] as number;
    cells[seed] = color;
    const group = [seed];
    for (let placed = 1; placed < CUBES_PER_COLOR; placed += 1) {
      const frontier = freeFrontier(group, cells);
      if (frontier.length === 0) return { cells: null, state };
      const step = nextRng(state);
      state = step.state;
      const next = frontier[Math.floor(step.value * frontier.length)] as number;
      cells[next] = color;
      group.push(next);
    }
  }
  return { cells, state };
}

/**
 * Deterministically generate a valid arrangement from `rngState`. Retries with
 * the advanced PRNG state when a blob gets boxed in, so the result is a pure
 * function of the seed. Termination is empirical: four 5-cell blobs in 27 cells
 * settle within a handful of attempts (verified for seeds 1–200); the 2000-cap
 * throw is a safety net that should never fire.
 */
export function generateArrangement(rngState: number): {
  arrangement: Arrangement;
  state: number;
} {
  let state = rngState;
  for (let attempt = 0; attempt < 2000; attempt += 1) {
    const result = tryBuildArrangement(state);
    state = result.state;
    if (result.cells !== null) {
      return { arrangement: result.cells, state };
    }
  }
  throw new Error("cube-hell: failed to generate a valid arrangement");
}

/** Validate a 27-cell arrangement: counts + per-colour 6-face connectivity. */
export function validateArrangement(
  arrangement: readonly Cell[],
): ValidationResult {
  if (arrangement.length !== CELL_COUNT) {
    return {
      valid: false,
      error: "Arrangement must have 27 cells",
      details: { length: arrangement.length },
    };
  }
  let empties = 0;
  const groups: Record<CubeColor, number[]> = {
    red: [],
    yellow: [],
    green: [],
    blue: [],
  };
  for (let i = 0; i < arrangement.length; i += 1) {
    const cell = arrangement[i];
    if (!isCell(cell)) {
      return {
        valid: false,
        error: "Invalid cell value",
        details: { i, cell },
      };
    }
    if (cell === "empty") empties += 1;
    else groups[cell].push(i);
  }
  if (empties !== EMPTY_CELLS) {
    return {
      valid: false,
      error: "Arrangement must have 7 empty cells",
      details: { empties },
    };
  }
  for (const color of CUBE_COLORS) {
    if (groups[color].length !== CUBES_PER_COLOR) {
      return {
        valid: false,
        error: `Colour ${color} must have 5 cubes`,
        details: { color, count: groups[color].length },
      };
    }
    if (!isConnectedGroup(groups[color])) {
      return {
        valid: false,
        error: `Colour ${color} is not 6-face connected`,
        details: { color, cells: groups[color] },
      };
    }
  }
  return { valid: true };
}

// ---- Query oracle (truthful) ------------------------------------------------

/** CELL query: the exact cell value at (x,y,z). */
export function queryCell(
  arrangement: readonly Cell[],
  x: number,
  y: number,
  z: number,
): Cell {
  return arrangement[cellIndex(x, y, z)] as Cell;
}

/** Cell indices of one 9-cell axis slice (ascending). */
export function layerCellIndices(axis: QueryAxis, index: number): number[] {
  const result: number[] = [];
  for (let i = 0; i < CELL_COUNT; i += 1) {
    if (axisValue(coordOf(i), axis) === index) result.push(i);
  }
  return result;
}

/** LAYER query: count of `color` in the axis/index slice (0..9). */
export function queryLayer(
  arrangement: readonly Cell[],
  axis: QueryAxis,
  index: number,
  color: CubeColor,
): number {
  let count = 0;
  for (const i of layerCellIndices(axis, index)) {
    if (arrangement[i] === color) count += 1;
  }
  return count;
}

// ---- Guess comparison -------------------------------------------------------

/** Number of cells where `guess` matches `actual` (0..27). */
export function countMatches(
  guess: readonly Cell[],
  actual: readonly Cell[],
): number {
  let matched = 0;
  for (let i = 0; i < CELL_COUNT; i += 1) {
    if (guess[i] === actual[i]) matched += 1;
  }
  return matched;
}

export function arrangementsEqual(
  a: readonly Cell[],
  b: readonly Cell[],
): boolean {
  return (
    a.length === CELL_COUNT &&
    b.length === CELL_COUNT &&
    countMatches(a, b) === CELL_COUNT
  );
}

// ---- Query budget -----------------------------------------------------------

/** Remaining queries for a player, or null when unlimited. */
export function remainingQueries(
  state: CubeHellState,
  playerId: CubeHellPlayerId,
): number | null {
  if (state.maxQueriesPerPlayer === null) return null;
  return Math.max(0, state.maxQueriesPerPlayer - state.queryCounts[playerId]);
}

// ---- Hidden-info projection -------------------------------------------------
/**
 * Symmetric viewer projection. A viewer sees their OWN arrangement; the
 * opponent's arrangement is masked (null). A null viewer (observer) sees both
 * masked. The PRNG state is dropped entirely — it never reaches the client. The
 * query/response log is public. Built field-by-field so no server-only data
 * leaks via object spread.
 */
export function filterStateForViewer(
  state: CubeHellState,
  viewerPlayerId: string | null,
): CubeHellViewerState {
  const projectArrangement = (owner: CubeHellPlayerId): Arrangement | null =>
    viewerPlayerId === owner ? state.arrangements[owner] : null;
  return {
    gameId: state.gameId,
    players: state.players,
    phase: state.phase,
    currentPlayerId: state.currentPlayerId,
    arrangements: {
      p1: projectArrangement("p1"),
      p2: projectArrangement("p2"),
    },
    log: state.log,
    queryCounts: state.queryCounts,
    winnerId: state.winnerId,
    lastAction: state.lastAction,
    moveNumber: state.moveNumber,
    guessPenalty: state.guessPenalty,
    maxQueriesPerPlayer: state.maxQueriesPerPlayer,
  };
}

export function buildViewerStates(
  state: CubeHellState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, CubeHellViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
